/* eslint-disable @typescript-eslint/no-namespace */
/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';
import classnames from 'classnames';
import { TypeAllElementTags, TypeColor, TypeSize } from '../types';
import './Box.css';

/**
 * The core component that is the end of the chain for all other components.
 * It provides general utility layout behaviour and styling.
 */
export interface BoxProps {
  /** Quick way of absolutely position to common places */
  absolute?:
    'center' |
    'edge-to-edge' |
    'top' |
    'top-left' |
    'top-right' |
    'bottom' |
    'bottom-left' |
    'bottom-right';
  /**
   * Short cut child alignment property for both alignChildrenHorizontal and
   * alignChildrenVertical.
   */
  alignChildren?: 'start' | 'middle' | 'end';
  /** Horizontal alignment of children flex items. */
  alignChildrenHorizontal?: 'start' | 'middle' | 'end' | 'around' | 'between';
  /** Vertical alignment of children flex items. */
  alignChildrenVertical?: 'start' | 'middle' | 'end' | 'around' | 'between';
  /** Flex item alignment property (changes with parent direction). */
  alignSelf?: 'start' | 'middle' | 'end';
  /** Background color */
  backgroundColor?: TypeColor;
  /** Initial width to flex from for a child item. */
  basis?: 'content' | 'container' | 'none';
  /** Border color */
  borderColor?: TypeColor;
  /** Adds styling of a border radius to one of the size multiples */
  borderRadius?: 'full' | 'x1' | 'x2' | 'x3';
  /** */
  borderSides?: 'LR' | 'L' | 'R' | 'TB' | 'T' | 'B';
  /** Thickness of the border to be applied */
  borderSize?: 'x1' | 'x2' | 'x3';
  /** Adds styling to indicate that the element is clickable */
  clickable?: boolean;
  /** */
  component?: React.ComponentType<any>;
  /** Applies relative positioning to contain child elements. */
  container?: boolean;
  /** Applies display styling */
  display?: 'block' | 'inline' | 'inline-block';
  /** Applies elevated styling */
  elevation?: 'x1' | 'x2';
  /** Quick way of fixed position to common places */
  fixed?:
    'center' |
    'edge-to-edge' |
    'top' |
    'top-left' |
    'top-right' |
    'bottom' |
    'bottom-left' |
    'bottom-right';
  /** Direction on which way to flex items. */
  flex?: 'horizontal' | 'vertical';
  /** Spacing applied between child flex items, values are global spacing variables. */
  gap?: TypeSize;
  /** Flex item property if it should grow with available space. */
  grow?: boolean;
  /** Fixed height applied through inline styling */
  height?: number | string;
  /** Margins applied for the global spacing variables */
  margin?: TypeSize;
  /** Max width applied through inline styling */
  maxHeight?: number | string;
  /** Max width applied through inline styling */
  maxWidth?: number | string;
  /** Centers content that has applied max width */
  maxWidthCentered?: boolean;
  /** Max height applied through inline styling */
  minHeight?: number | string;
  /** Min width applied through inline style */
  minWidth?: number | string;
  /** Sets how overflown content is handled */
  overflow?: 'hidden';
  /** Padding applied for the global spacing variables */
  padding?: TypeSize;
  /** Horizontal padding applied for the global spacing variables */
  paddingHorizontal?: TypeSize;
  /** Vertical pa?dding applied for the global spacing variables */
  paddingVertical?: TypeSize;
  /** Applies overflow styling to enable/disable scrolling.  */
  scrollable?: boolean;
  /** Flex item property if it should shrink. */
  shrink?: boolean;
  /**
   * Any valid HTML or SVG element tag.
   *
   * @reference false
   */
  tag?: TypeAllElementTags;
  /** Text color */
  textColor?: TypeColor;
  /** Fixed width applied through inline styling */
  width?: number | string;
  /** Flag to allow flex items to wrap over to new lines */
  wrap?: boolean;
  /** z-index number for layering elements.  */
  zIndex?: number;
}

type ReactElementProps = Omit<
  React.AllHTMLAttributes<Element> &
  React.SVGAttributes<Element>
, 'crossOrigin' | 'wrap'>;

const Box: React.ForwardRefRenderFunction<Element, BoxProps & ReactElementProps> = (props, ref) => {
  const {
    absolute,
    alignChildren,
    alignChildrenHorizontal = alignChildren,
    alignChildrenVertical = alignChildren,
    alignSelf,
    backgroundColor,
    basis,
    borderColor,
    borderRadius,
    borderSides,
    borderSize,
    children,
    className,
    clickable,
    component,
    container,
    display,
    elevation,
    fixed,
    flex,
    grow,
    gap,
    height,
    maxHeight,
    maxWidth,
    maxWidthCentered,
    minWidth,
    minHeight,
    overflow,
    padding,
    paddingHorizontal = padding,
    paddingVertical = padding,
    margin,
    scrollable,
    shrink,
    tag = 'div',
    textColor,
    width,
    wrap,
    zIndex,
    ...rest
  } = props;

  const classes = classnames('Box', {
    'Box--clickable': clickable,
    'Box--container': container,
    'Box--flex-gapped': gap,
    'Box--flex-grow': grow,
    'Box--flex-shrink': shrink,
    'Box--flex-wrap': wrap,
    'Box--max-width': maxWidth,
    'Box--max-width-centered': maxWidthCentered,
    'Box--scrollable': scrollable,
    [`Box--absolute-${absolute}`]: absolute,
    [`Box--background-color-${backgroundColor}`]: backgroundColor,
    [`Box--border-color-${borderColor}`]: borderColor,
    [`Box--border-radius-${borderRadius}`]: borderRadius,
    [`Box--border-sides-${borderSides}`]: borderSides,
    [`Box--border-size-${borderSize}`]: borderSize,
    [`Box--display-${display}`]: display,
    [`Box--elevation-${elevation}`]: elevation,
    [`Box--flex-basis-${basis}`]: basis,
    [`Box--fixed-${fixed}`]: fixed,
    [`Box--flex-${flex}`]: flex,
    [`Box--flex-align-horz-${alignChildrenHorizontal}`]: alignChildrenHorizontal,
    [`Box--flex-align-self-${alignSelf}`]: alignSelf,
    [`Box--flex-align-vert-${alignChildrenVertical}`]: alignChildrenVertical,
    [`Box--flex-gap-${gap}`]: gap,
    [`Box--margin-${margin}`]: margin,
    [`Box--overflow-${overflow}`]: overflow,
    [`Box--padding-horizontal-${paddingHorizontal}`]: paddingHorizontal,
    [`Box--padding-vertical-${paddingVertical}`]: paddingVertical,
    [`Box--text-color-${textColor}`]: textColor,
  }, className);

  const style = {
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    width,
    zIndex,
    ...props.style,
  };

  return React.createElement(component || tag, {
    ...rest,
    className: classes,
    ref: ref,
    style: style,
  }, children);
};

export default React.forwardRef(Box);
